'use client'

import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import {
  BalanceDataGridFilter,
  ClaimsStatusFilter,
  DateDataGridFilter,
  TextFieldDataGridFilter,
  ClaimRecipientFilter
} from '@repo/clients-matters-tables/DataGridTableCustomFilters'
import { ChatIcon } from '@repo/ui/ChatIcon'
import { ClaimsMoreMenuCard } from '@repo/claims-tables/ClaimsMoreMenuCard'
import { ClaimStatusDropdownButton } from '@repo/ui/ClaimStatusDropdownButton'
import { ClaimsToolbarModule } from '@repo/claims-tables/ClaimsToolbarModule'
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  gridColumnDefinitionsSelector,
  GridColumnMenu,
  GridColumnMenuProps,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridRowId,
  GridRenderHeaderFilterProps,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { DataGridSelectionTray } from '@repo/ui/DataGridSelectionTray'
import { formatCurrency } from '@repo/utils/numbers'
import { formatDate } from '@repo/utils/time'
import { Pill } from '@repo/ui/Pill'
import { TableClearFilters } from '@repo/ui/TableClearFilters'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useCommentsStore } from '@repo/store/useCommentsStore'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useNavigate } from 'react-router-dom'
import { useOvertimesStore } from '@repo/store/useOvertimesStore'
import { useToastStore } from '@repo/store/useToastStore'
import {
  getDisabledStatusesFromPermissions,
  getDisabledStatusesFromCurrentStatuses,
  mergeDisabledStatuses
} from '@repo/utils/claimPermissions'
import { useTheme } from '@mui/material/styles'
import { useUserPreferencesStore } from '@repo/store/useUserPreferencesStore'
import { useWindowSize } from '@uidotdev/usehooks'
import { v4 as uuidv4 } from 'uuid'
import defaultOvertimeTableConfig from '@repo/store/defaultOvertimeTableConfig'
import MUIButton from '@mui/material/Button'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

const parseMaybeJson = (val: unknown) => {
  if (!val) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch {
      return {}
    }
  }
  if (typeof val === 'object') return val as Record<string, any>
  return {}
}

export const OvertimeTableModule = ({ isSubmissionVersion }: OvertimeTableModuleProps) => {
  const apiRef = useGridApiRef()
  const navigate = useNavigate()

   const { allowedActions } = useMenuItemsStore()
  const { setOvertimeClaimPermissions, submitOvertimeClaims, processOvertimeClaims } = allowedActions

  const theme = useTheme()

  const { userId } = useAuthenticationStore()
  const {
    isClaimsDrawerOpen,
    openNewClaimDrawer,
    setIsClaimsDrawerOpen,
    fetchAllOvertimeUserPermissionsByProcessorUserId
  } = useClaimsStore()
  const { fetchAllOvertimeClaimComments, commentsUpdate } = useCommentsStore()
  const {
    fetchAllExtendedOvertimeClaims,
    fetchAllExtendedOvertimeClaimsAsCSV,
    updateOvertimeClaimStatuses,
    overtimesTableUpdate
  } = useOvertimesStore()

  const {
    setOvertimeTableFilters,
    setOvertimeTableFiltersFromPreferences,
    overtimeTableFilters,
    resetOvertimeTableFilters,
    overtimeTableFilterCount
  } = useDataGridFilterStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { 'Expenses and Overtime': overtimePreferences, updateUserPreference } = useUserPreferencesStore()

  const { height: windowHeight } = useWindowSize()
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 216 : 0)
  const [filtersReady, setFiltersReady] = useState(false)
  const [claimStatus, setClaimStatus] = useState<number | null>(null)
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({})
  const commentCountsRef = useRef(commentCounts)
  commentCountsRef.current = commentCounts
  const [overtime, setOvertime] = useState([])
  const [permissions, setPermissions] = useState<any[]>([])
  const [permissionsLoading, setPermissionsLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [totalRowCount, setTotalRowCount] = useState(0)

  const activePref = useMemo(
    () => overtimePreferences?.OvertimeClaim_GetAllExtendedBySearchCriteria,
    [overtimePreferences]
  )

  const resolvedConfig = useMemo(() => {
    const rv = activePref?.resolvedValue ?? activePref?.userValue ?? activePref?.defaultValue ?? {}
    return parseMaybeJson(rv)
  }, [activePref])

  const activePrefId = activePref?.userConfigurationTypeId ?? null

  const fallback = defaultOvertimeTableConfig || {}

  const [loading, setLoading] = useState(false)
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({})
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({ left: [], right: [] })
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  const [selectedRowsById, setSelectedRowsById] = useState<Record<string, OvertimeRow>>({})

  const selectedRows = useMemo(() => Object.values(selectedRowsById), [selectedRowsById])

  useEffect(() => {
    if (!userId || isSubmissionVersion) {
      setPermissionsLoading(false)
      return
    }

    let cancelled = false
    setPermissionsLoading(true)

    fetchAllOvertimeUserPermissionsByProcessorUserId({ processorUserId: userId })
      .then((result: any) => {
        if (!cancelled) setPermissions(Array.isArray(result) ? result : [])
      })
      .catch((error: any) => {
        console.error('Error fetching overtime user permissions:', error)
        if (!cancelled) setPermissions([])
      })
      .finally(() => {
        if (!cancelled) setPermissionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [userId, isSubmissionVersion, fetchAllOvertimeUserPermissionsByProcessorUserId])

  const { disabledStatuses, tooltips: statusTooltips } = useMemo(() => {
    if (isSubmissionVersion) {
      return { disabledStatuses: [] as number[], tooltips: {} as Record<number, string> }
    }

    if (permissionsLoading || selectedRows.length === 0) {
      return getDisabledStatusesFromPermissions([], '')
    }

    const recipientIds = [
      ...new Set(selectedRows.map((row) => row.claimRecipientUserId).filter((id): id is string => !!id))
    ]

    let permResult
    if (permissions.length === 0) {
      const disabledStatuses = [0]
      const tooltips: Record<number, string> = { 0: 'Claims cannot be changed to draft status' }

      if (!submitOvertimeClaims?.allowed) {
        ;[1, 2, 3, 4].forEach((s) => {
          disabledStatuses.push(s)
          tooltips[s] = 'You do not have permission to approve claims'
        })
      }

      if (!processOvertimeClaims?.allowed) {
        ;[5, 6, 7, 8].forEach((s) => {
          disabledStatuses.push(s)
          tooltips[s] = 'You do not have permission to process payments'
        })
      }

      permResult = { disabledStatuses, tooltips }
    } else {
      permResult = getDisabledStatusesFromPermissions(permissions, recipientIds)
    }

    const currentStatuses = selectedRows
      .map((row) => row.status)
      .filter((s): s is number => s !== undefined && s !== null)
    const statusResult = getDisabledStatusesFromCurrentStatuses(currentStatuses)

    return mergeDisabledStatuses(permResult, statusResult)
  }, [permissions, permissionsLoading, selectedRows, isSubmissionVersion, submitOvertimeClaims?.allowed, processOvertimeClaims?.allowed])

  useEffect(() => {
    if (claimStatus !== null && disabledStatuses.includes(claimStatus)) {
      setClaimStatus(null)
    }
  }, [disabledStatuses, claimStatus])

  const OPERATOR_KEYS = new Set([
    'numberComparisonOperator',
    'dateComparisonOperator',
    'comparisonOperator',
    'operator'
  ])

  const isEmptyPrimitive = (x: unknown) => x === null || x === undefined || (typeof x === 'string' && x.trim() === '')

  const isFilterValueActive = (v: any): boolean => {
    if (isEmptyPrimitive(v)) return false
    if (Array.isArray(v)) return v.some((item) => !isEmptyPrimitive(item))
    switch (typeof v) {
      case 'boolean':
        return true
      case 'number':
        return true
      case 'string':
        return v.trim() !== ''
      case 'object': {
        if ('value' in v) return !isEmptyPrimitive((v as any).value)
        if ('date' in v) return !isEmptyPrimitive((v as any).date)
        return Object.entries(v as Record<string, any>).some(
          ([k, val]) => !OPERATOR_KEYS.has(k) && isFilterValueActive(val)
        )
      }
      default:
        return false
    }
  }

  const areFiltersEffectivelyEmpty = (f: Record<string, any> | null | undefined) => {
    if (!f) return true
    return !Object.values(f).some((v) => isFilterValueActive(v))
  }

  const emptyOutFilters = (f: Record<string, any> | null | undefined) => {
    if (!f) return {}
    const out: Record<string, any> = {}
    for (const [k, v] of Object.entries(f)) {
      if (v && typeof v === 'object') {
        if ('date' in v || 'dateComparisonOperator' in v) {
          out[k] = { date: null, dateComparisonOperator: 0 }
        } else if ('value' in v || 'numberComparisonOperator' in v) {
          out[k] = { value: null, numberComparisonOperator: 0 }
        } else {
          out[k] = null
        }
      } else {
        out[k] = null
      }
    }
    return out
  }

  const handleRowSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setRowSelectionModel(newSelection)

      setSelectedRowsById((prev) => {
        const next: Record<string, OvertimeRow> = {}

        for (const id of newSelection as GridRowId[]) {
          const key = String(id)
          const rowFromGrid = apiRef.current.getRow(id) as OvertimeRow | null
          const row = rowFromGrid ?? prev[key]
          if (row) next[key] = row
        }

        return next
      })
    },
    [apiRef]
  )

  const trayHeight = 72
  const trayOpen = !isSubmissionVersion && selectedRows.length > 0

  const prefsHydratedRef = useRef(false)
  const firstSyncDoneRef = useRef(false)
  const userClearedRef = useRef(false)
  const configHydratedRef = useRef(false)

  useEffect(() => {
    const prefBag = resolvedConfig?.filters ?? null
    const storeEmpty = areFiltersEffectivelyEmpty(overtimeTableFilters?.filters)
    const prefsEmpty = areFiltersEffectivelyEmpty(prefBag)

    if (prefsHydratedRef.current) {
      setFiltersReady(true)
      return
    }

    if (!storeEmpty) {
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    if (!prefsEmpty) {
      setOvertimeTableFiltersFromPreferences({ filters: prefBag ?? null })
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    setFiltersReady(true)
  }, [resolvedConfig?.filters, overtimeTableFilters?.filters, setOvertimeTableFiltersFromPreferences])

  useEffect(() => {
    if (configHydratedRef.current) return

    setColumnOrder(resolvedConfig.columnOrder ?? fallback.columnOrder ?? [])
    setColumnVisibilityModel(resolvedConfig.columnVisibilityModel ?? fallback.columnVisibilityModel ?? {})
    setColumnWidths(resolvedConfig.columnWidths ?? fallback.columnWidths ?? {})
    setPinnedColumns({
      left: resolvedConfig.columnsPinnedLeft ?? fallback.columnsPinnedLeft ?? [],
      right: resolvedConfig.columnsPinnedRight ?? fallback.columnsPinnedRight ?? []
    })
    setSortModel(resolvedConfig.columnSort ?? fallback.columnSort ?? [{ field: 'date', sort: 'desc' as const }])

    if (Object.keys(resolvedConfig).length > 0) {
      configHydratedRef.current = true
    }
  }, [resolvedConfig])

  const useDeepCompareMemoize = (value: any) => {
    const ref = useRef(value)
    const signal = JSON.stringify(value)
    const prevSignal = JSON.stringify(ref.current)
    if (signal !== prevSignal) {
      ref.current = value
    }
    return ref.current
  }

  const deepFilters = useDeepCompareMemoize(overtimeTableFilters)

  // Fetch overtime data when filters change
  useEffect(() => {
    if (!filtersReady) return

    const getOvertimeData = async () => {
      setLoading(true)

      const filters = deepFilters?.filters || {}

      const computedOrderBy = sortModel
        .map((sortItem) => {
          const fieldMap: Record<string, string> = {
            date: 'incurredDate',
            claimAmount: 'amount'
          }
          const columnName = fieldMap[sortItem.field] ?? sortItem.field
          return JSON.stringify({ columnName, direction: sortItem.sort === 'asc' ? 0 : 1 })
        })
        .filter(Boolean)

      const orderByColumns = computedOrderBy.length > 0 ? computedOrderBy : null

      const deepFiltersFiltersString = JSON.stringify(deepFilters.filters)
      const currentTableConfigFiltersString = JSON.stringify(resolvedConfig.filters)
      const filtersHaveChanged = deepFiltersFiltersString !== currentTableConfigFiltersString

      const safeToSync = firstSyncDoneRef.current

      if (safeToSync && filtersHaveChanged) {
        updateTableConfigPreference({ filters: deepFilters.filters })
      }

      let associatedClientNo = filters.clientNo || null
      let associatedMatterNo = filters.matterNo || null
      if (filters.associatedMatter) {
        const parts = filters.associatedMatter.split('-')
        if (parts.length >= 2) {
          associatedMatterNo = parts.pop() || null
          associatedClientNo = parts.join('-') || null
        } else {
          associatedClientNo = filters.associatedMatter
        }
      }

      try {
        const response = await fetchAllExtendedOvertimeClaims({
          amountNumberComparisonOperator: filters.claimAmount?.value
            ? (filters.claimAmount?.numberComparisonOperator ?? 0)
            : null,
          amountValue: filters.claimAmount?.value ?? null,
          claimRecipientUserIds: filters.claimRecipient || null,
          clientNo: associatedClientNo,
          description: filters.claimDescription || null,
          incurredDate: filters.dateIncurred?.date || null,
          incurredDateComparisonOperator: filters.dateIncurred?.dateComparisonOperator ?? 0,
          isSubmission: isSubmissionVersion || false,
          matterNo: associatedMatterNo,
          orderByColumns,
          overtimeClaimTypeIds: filters.claimType || null,
          pageSize: deepFilters?.pageSize ?? 25,
          startAtIndex: deepFilters?.startAtIndex ?? 0,
          statuses:
            filters.status != null
              ? Array.isArray(filters.status)
                ? filters.status.length > 0
                  ? filters.status
                  : null
                : [filters.status]
              : null
        })

        setOvertime(response?.rows || [])
        if (response?.totalRowCount !== undefined) {
          setTotalRowCount(response.totalRowCount)
        }
      } catch (error) {
        console.error('Error fetching overtime claims:', error)
      } finally {
        setLoading(false)
      }

      if (!firstSyncDoneRef.current) firstSyncDoneRef.current = true
    }

    getOvertimeData()
  }, [
    filtersReady,
    deepFilters.pageSize,
    deepFilters.filters,
    deepFilters.startAtIndex,
    sortModel,
    isSubmissionVersion,
    fetchAllExtendedOvertimeClaims,
    overtimesTableUpdate
  ])

  useEffect(() => {
    if (!overtime || overtime.length === 0) return
    if (isClaimsDrawerOpen) return

    const fetchCommentCounts = async () => {
      try {
        const response = await fetchAllOvertimeClaimComments()
        const counts: Record<string, number> = {}
        if (Array.isArray(response)) {
          response.forEach((comment: any) => {
            const id = comment.overtimeClaimId
            if (id) counts[id] = (counts[id] || 0) + 1
          })
        }
        setCommentCounts(counts)
      } catch {
        setCommentCounts({})
      }
    }

    fetchCommentCounts()
  }, [overtime, fetchAllOvertimeClaimComments, commentsUpdate, isClaimsDrawerOpen])

  const updateTableConfigPreference = useCallback(
    (fieldsToChange: any) => {
      if (!activePrefId) return

      updateUserPreference({
        userConfigurationTypeId: activePrefId,
        value: {
          ...resolvedConfig,
          columnOrder: columnOrder,
          columnVisibilityModel: columnVisibilityModel,
          columnWidths: columnWidths,
          columnsPinnedLeft: pinnedColumns.left,
          columnsPinnedRight: pinnedColumns.right,
          columnSort: sortModel,
          ...fieldsToChange
        },
        convertJsonToString: true,
        categoryName: 'Expenses and Overtime'
      })
    },
    [
      activePrefId,
      resolvedConfig,
      updateUserPreference,
      sortModel,
      columnOrder,
      columnVisibilityModel,
      columnWidths,
      pinnedColumns
    ]
  )

  const onPinnedColumnsChange = useCallback((updatedPinnedColumns: GridPinnedColumnFields) => {
    setPinnedColumns(updatedPinnedColumns)
    updateTableConfigPreference({
      columnsPinnedLeft: updatedPinnedColumns.left,
      columnsPinnedRight: updatedPinnedColumns.right
    })
  }, [])

  const onSortModelChange = (model: GridSortModel) => {
    setSortModel(model)
    updateTableConfigPreference({ columnSort: model })
  }

  const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model)
    updateTableConfigPreference({ columnVisibilityModel: model })
  }

  const onColumnOrderChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const newColumnOrder = columnDefinitions.map((column: any) => column.field)
    setColumnOrder(newColumnOrder)
    updateTableConfigPreference({ columnOrder: newColumnOrder })
  }

  const onColumnWidthChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const newColumnWidths: Record<string, number> = {}
    columnDefinitions.forEach((column: any) => {
      newColumnWidths[column.field] = column.width
    })
    setColumnWidths(newColumnWidths)
    updateTableConfigPreference({ columnWidths: newColumnWidths })
  }

  const handleClearAll = useCallback(() => {
    userClearedRef.current = true
    resetOvertimeTableFilters()
    const emptied = emptyOutFilters(resolvedConfig?.filters ?? overtimeTableFilters?.filters ?? {})
    updateTableConfigPreference({ filters: emptied })
  }, [resetOvertimeTableFilters, resolvedConfig?.filters, overtimeTableFilters?.filters, updateTableConfigPreference])

  const handleNewClaim = useCallback(() => {
    openNewClaimDrawer(false, true) // isExpensesTable = false, openDirectToForm = true
  }, [openNewClaimDrawer])

  const handleSubmitDrafts = useCallback(() => {
    openNewClaimDrawer(false) // isExpensesTable = false
  }, [openNewClaimDrawer])

  const handleExportCsv = useCallback(async () => {
    const filters = overtimeTableFilters?.filters || {}

    const computedOrderBy = sortModel
      .map((sortItem) => {
        const fieldMap: Record<string, string> = {
          date: 'incurredDate',
          claimAmount: 'amount'
        }
        const columnName = fieldMap[sortItem.field] ?? sortItem.field
        return { columnName, direction: sortItem.sort === 'asc' ? 0 : 1 }
      })
      .filter(Boolean)

    const orderByColumns = computedOrderBy.length > 0 ? computedOrderBy : null

    let associatedClientNo = filters.clientNo || null
    let associatedMatterNo = filters.matterNo || null
    if (filters.associatedMatter) {
      const parts = filters.associatedMatter.split('-')
      if (parts.length >= 2) {
        associatedMatterNo = parts.pop() || null
        associatedClientNo = parts.join('-') || null
      } else {
        associatedClientNo = filters.associatedMatter
      }
    }

    try {
      await fetchAllExtendedOvertimeClaimsAsCSV({
        amountNumberComparisonOperator: filters.claimAmount?.value
          ? (filters.claimAmount?.numberComparisonOperator ?? 0)
          : null,
        amountValue: filters.claimAmount?.value ?? null,
        claimRecipientUserIds: filters.claimRecipient || null,
        clientNo: associatedClientNo,
        description: filters.claimDescription || null,
        incurredDate: filters.dateIncurred?.date || null,
        incurredDateComparisonOperator: filters.dateIncurred?.dateComparisonOperator ?? 0,
        isSubmission: isSubmissionVersion || false,
        matterNo: associatedMatterNo,
        orderByColumns,
        overtimeClaimTypeIds: filters.claimType || null,
        pageSize: overtimeTableFilters?.pageSize ?? 25,
        startAtIndex: overtimeTableFilters?.startAtIndex ?? 0,
        statuses:
          filters.status != null
            ? Array.isArray(filters.status)
              ? filters.status.length > 0
                ? filters.status
                : null
              : [filters.status]
            : null
      })
    } catch (error) {
      console.error('Error exporting overtime claims as CSV:', error)
    }
  }, [overtimeTableFilters, sortModel, isSubmissionVersion, fetchAllExtendedOvertimeClaimsAsCSV])

  const columns: any = useMemo(
    () => [
      {
        field: 'incurredDate',
        id: 'incurredDate',
        headerName: 'Date Incurred',
        type: 'custom',
        width: columnWidths?.['incurredDate'] || 150,
        flex: 0.65,
        filterable: true,
        renderCell: (params: any) =>
          params.value ? (
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              {formatDate(params.value)}
            </MUITypography>
          ) : (
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              -
            </MUITypography>
          ),
        renderHeaderFilter: () => <DateDataGridFilter filterType="dateIncurred" isOvertimeTable />
      },
      {
        field: 'claimRecipientUserId',
        id: 'claimRecipientUserId',
        headerName: 'Claim Recipient',
        type: 'custom',
        width: columnWidths?.['claimRecipientUserId'] || 200,
        flex: 1,
        filterable: !isSubmissionVersion,
        renderHeaderFilter: isSubmissionVersion
          ? () => null
          : (params: GridRenderHeaderFilterProps) => (
              <ClaimRecipientFilter
                filterType="claimRecipient"
                isExpensesTable={false}
                testId="claim-recipient-filter"
                {...params}
              />
            ),
        renderCell: (params: any) => (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {params.value || '-'}
          </MUITypography>
        )
      },
      {
        field: 'overtimeClaimTypeDescription',
        id: 'overtimeClaimTypeDescription',
        headerName: 'Claim Type',
        type: 'string',
        width: columnWidths?.['overtimeClaimTypeDescription'] || 200,
        flex: 0.65,
        filterable: false,
        renderCell: (params: any) => (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {params.value || '-'}
          </MUITypography>
        )
      },
      {
        field: 'description',
        id: 'description',
        headerName: 'Claim Description',
        type: 'string',
        width: columnWidths?.['description'] || 300,
        flex: 1,
        renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
          <TextFieldDataGridFilter filterType="claimDescription" isOvertimeTable {...params} />
        ),
        renderCell: (params: any) => (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {params.value || '-'}
          </MUITypography>
        )
      },
      {
        field: 'associatedMatter',
        id: 'associatedMatter',
        headerName: 'Assoc Matter',
        type: 'custom',
        width: columnWidths?.['associatedMatter'] || 150,
        flex: 0.5,
        renderCell: (params: any) => (
          <ClientMatterDetailsPill matterNo={params.row.matterNo} clientNo={params.row.clientNo} sx={{ top: '3px' }} />
        ),
        renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
          <TextFieldDataGridFilter filterType="associatedMatter" isOvertimeTable {...params} />
        )
      },
      {
        field: 'amount',
        id: 'amount',
        headerName: 'Claim Amount',
        type: 'custom',
        width: columnWidths?.['amount'] || 200,
        flex: 0.65,
        renderCell: (params: any) => (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {formatCurrency(params.value || 0) || '-'}
          </MUITypography>
        ),
        renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
          <BalanceDataGridFilter filterType="claimAmount" isOvertimeTable {...params} />
        )
      },
      {
        field: 'hours',
        id: 'hours',
        headerName: 'Overtime Hours',
        type: 'custom',
        filterable: false,
        width: columnWidths?.['hours'] || 150,
        flex: 0.5,
        // renderHeaderFilter: () => <BalanceDataGridFilter filterType="overtimeHours" isOvertimeTable />,
        renderCell: (params: any) => (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {params.value ?? '-'}
          </MUITypography>
        )
      },
      {
        field: 'status',
        id: 'status',
        headerName: 'Status',
        type: 'singleSelect',
        width: columnWidths?.['status'] || 200,
        flex: 0.65,
        renderCell: (params: any) => (
          <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
            {params.value === 0 ? (
              <Pill
                label="DRAFT"
                color={'black'}
                backgroundColor={'#DFE0E4'}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
            {params.value === 1 ? (
              <Pill
                label="SUBMITTED"
                color={'black'}
                backgroundColor={'#DFE0E4'}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
            {params.value === 2 ? (
              <Pill
                label="APPROVED"
                color={'white'}
                backgroundColor={'#377F86'}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
            {params.value === 3 ? (
              <Pill
                label="REJECTED"
                color={'white'}
                backgroundColor={theme.palette.error.main}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
            {params.value === 4 ? (
              <Pill
                label="ACTION REQUIRED"
                color={'white'}
                backgroundColor={'#DE6A1E'}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
            {params.value === 5 ? (
              <Pill
                label="READY TO PAY"
                color={'white'}
                backgroundColor={'#5073C3'}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
            {params.value === 6 ? (
              <Pill
                label="POSTED"
                color={'white'}
                backgroundColor={'#647495'}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
            {params.value === 7 ? (
              <Pill
                label="PAID"
                color={'white'}
                backgroundColor={'#051C33'}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
            {params.value === 8 ? (
              <Pill
                label="TOIL AGREED"
                color={'white'}
                backgroundColor={'#051C33'}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            ) : null}
          </MUIStack>
        ),
        renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
          <ClaimsStatusFilter
            isExpensesTable={false}
            isOvertimeTable
            isSubmissionVersion={isSubmissionVersion}
            {...params}
          />
        )
      },
      {
        field: 'comments',
        id: 'comments',
        headerName: '',
        type: 'custom',
        width: columnWidths?.['comments'] || 100,
        flex: 0.35,
        sortable: false,
        hideable: false,
        filterable: false,
        renderCell: (params: any) => {
          const id = params.row.overtimeClaimId
          const count = commentCountsRef.current[id] || 0
          return (
            <MUIStack display="flex" direction="row" justifyContent="center" alignItems="center">
              {count > 0 ? (
                <>
                  <ChatIcon sx={{ height: '18px', width: '18px', position: 'relative', mr: 2 }} />
                  <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
                    {count}
                  </MUITypography>
                </>
              ) : (
                <MUITypography variant="body2" color="text.secondary" sx={{ mb: 0, fontSize: '0.875rem' }}>
                  {' '}
                  -
                </MUITypography>
              )}
            </MUIStack>
          )
        }
      },
      {
        field: 'moreMenu',
        id: 'moreMenu',
        headerName: '',
        type: 'actions',
        width: columnWidths?.['moreMenu'] || 49,
        sortable: false,
        hideable: false,
        filterable: false,
        renderCell: (params: any) => (
          <ClaimsMoreMenuCard isSubmissionVersion={isSubmissionVersion} rowData={params.row} isExpensesTable={false} />
        )
      }
    ],
    [columnWidths, isSubmissionVersion, theme]
  )

  const sortedColumns = useMemo(
    () =>
      columnOrder?.length > 0
        ? [...columns].sort((a, b) => {
            const targetIndexA = columnOrder.indexOf(a.field)
            const targetIndexB = columnOrder.indexOf(b.field)
            if (targetIndexA === -1 && targetIndexB === -1) return 0
            if (targetIndexA === -1) return 1
            if (targetIndexB === -1) return -1
            return targetIndexA - targetIndexB
          })
        : columns,
    [columns, columnOrder]
  )

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => column.hideable !== false && column.field !== '__check__')
      .map((column) => column.field)
  }

  const paginationModel: GridPaginationModel = useMemo(
    () => ({
      page: Math.floor((overtimeTableFilters?.startAtIndex ?? 0) / (overtimeTableFilters?.pageSize ?? 25)),
      pageSize: overtimeTableFilters?.pageSize ?? 25
    }),
    [overtimeTableFilters?.startAtIndex, overtimeTableFilters?.pageSize]
  )

  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 280 : 176
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  return (
    <MUIPaper
      key={`overtime-table-module`}
      data-testid="OvertimeTableModule"
      sx={{
        position: 'relative',
        fontSize: '0.875rem',
        height: trayOpen ? `calc(${tableHeight}px - ${trayHeight + 16}px)` : `calc(${tableHeight}px - 20px)` || 'auto',
        width: '100%',
        mt: pageTitleBlockIsVisible ? '9rem' : '0rem',
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)',
        [`@media (min-height: 991px)`]: { mt: '-1.5rem' },
        [`& .MuiDataGrid-root`]: {
          border: 'none',
          mb: 0,
          mt: pageTitleBlockIsVisible ? '2rem' : 0,
          [`& div[field="clientNames"]`]: { cursor: 'pointer', [`& .MuiStack-root`]: { cursor: 'pointer' } }
        },
        '& .MuiDataGrid-columnsPanel .MuiDataGrid-panelHeader': {
          display: 'none'
        }
      }}
    >
      {filtersReady && (
        <>
          <TableClearFilters
            filterCount={overtimeTableFilterCount}
            onClear={handleClearAll}
            sx={{ position: 'absolute', top: '3px', right: '130px', zIndex: 2 }}
          />
          <DataGridPro
            key={`overtime-grid-${isSubmissionVersion ? 'submission' : 'approval'}`}
            apiRef={apiRef}
            checkboxSelection={!isSubmissionVersion}
            columns={sortedColumns as GridColDef[]}
            columnVisibilityModel={columnVisibilityModel}
            disableRowSelectionOnClick
            disableVirtualization
            getRowId={(row) => row.overtimeClaimId || uuidv4()}
            headerFilters
            loading={loading}
            onCellClick={(params, event) => {
              if (params.field === '__check__' || params.field === 'moreMenu') return
              const overtimeId = params.row.overtimeClaimId
              if (!overtimeId) return
              if (params.field === 'comments') {
                setIsClaimsDrawerOpen(
                  false,
                  { overtimeClaimId: overtimeId, status: params.row.status },
                  isSubmissionVersion,
                  'chat'
                )
                return
              }
              if (isSubmissionVersion) return
              const navState = { status: params.row.status }
              navigate(`/accounts/employee-claims/overtime-claims/overtime-review/${overtimeId}`, { state: navState })
            }}
            onColumnOrderChange={onColumnOrderChange}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            onColumnWidthChange={onColumnWidthChange}
            onPaginationModelChange={(newModel) => {
              setOvertimeTableFilters({
                pageSize: newModel.pageSize,
                startAtIndex: newModel.page * newModel.pageSize
              })
            }}
            onPinnedColumnsChange={onPinnedColumnsChange}
            onRowSelectionModelChange={handleRowSelectionChange}
            onSortModelChange={onSortModelChange}
            pageSizeOptions={[5, 10, 25, 50]}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            pinnedColumns={pinnedColumns}
            rowCount={totalRowCount}
            rows={overtime || []}
            rowSelectionModel={rowSelectionModel}
            slotProps={{
              columnsManagement: { getTogglableColumns },
              loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
            }}
            sortModel={sortModel}
            sx={{
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                outline: 'none'
              },
              [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' },
              [`& .MuiDataGrid-cell`]: { alignContent: 'center', [`& .MuiTypography-root`]: { top: 'unset' } },
              [`& .MuiTablePagination-selectLabel`]: {
                display: 'block !important'
              },
              [`& .MuiTablePagination-input`]: {
                display: 'inline-flex !important',
                mb: 0,
                ml: 0,
                mt: '8px',
                fontSize: '0.875rem',
                [`& .MuiTablePagination-select`]: {
                  padding: '0 !important',
                  ml: '4px',
                  mr: '20px',
                  minWidth: 'none',
                  fontSize: '12px',
                  [`&:focus`]: { backgroundColor: 'transparent' }
                },
                [`& .MuiSvgIcon-root`]: { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
              },
              [`& .MuiTablePagination-actions`]: { [`& .MuiButtonBase-root`]: { padding: 0, top: '4px' } },
              [`& .user-restricted-header-name`]: { [`& .MuiDataGrid-columnHeaderTitle`]: { display: 'none' } },
              [`& .MuiDataGrid-row`]: {
                cursor: isSubmissionVersion ? 'default' : 'pointer'
              },
              ...(isSubmissionVersion && {
                [`& .MuiDataGrid-cell[data-field="comments"], & .MuiDataGrid-cell[data-field="moreMenu"]`]: {
                  cursor: 'pointer'
                }
              }),
              [`& .MuiDataGrid-filler`]: {
                display: 'none'
              }
            }}
            slots={{
              columnMenu: (props: GridColumnMenuProps) => (
                <GridColumnMenu
                  {...props}
                  // @ts-ignore
                  sx={{ '.MuiTypography-body1': { mb: 0 } }}
                  slots={{ columnMenuFilterItem: null }}
                />
              ),
              headerFilterMenu: null,
              toolbar: () => (
                <ClaimsToolbarModule
                  showNewClaim={isSubmissionVersion}
                  onNewClaim={handleNewClaim}
                  onSubmitDrafts={handleSubmitDrafts}
                  onExportCsv={handleExportCsv}
                  showManagePermissions={!isSubmissionVersion && setOvertimeClaimPermissions?.allowed === true}
                  onManagePermissions={() => navigate('/accounts/employee-claims/overtime-claims/permissions')}
                  isOvertimeTable
                />
              )
            }}
          />
        </>
      )}
      {!isSubmissionVersion && (
        <DataGridSelectionTray<OvertimeRow> selectedRows={selectedRows} height={trayHeight}>
          {({ selectedRows }) => (
            <>
              <MUITypography variant="body2" sx={{ whiteSpace: 'nowrap', mb: 0 }}>
                Mark selected items as:
              </MUITypography>
              <ClaimStatusDropdownButton
                value={claimStatus}
                onChange={(newStatus) => {
                  setClaimStatus(newStatus)
                }}
                top={true}
                hiddenStatuses={[0]}
                disabledStatuses={disabledStatuses}
                statusTooltips={statusTooltips}
                sx={{ minWidth: 200 }}
              />
              <MUIButton
                disabled={claimStatus === null || saving}
                size="large"
                variant="outlined"
                onClick={async () => {
                  const overtimeClaimIds = selectedRows
                    .map((row) => row.overtimeClaimId)
                    .filter((id): id is string => id !== undefined)

                  if (overtimeClaimIds.length > 0 && claimStatus !== null) {
                    const claimIdsWithoutClientMatter = selectedRows
                      .filter((row) => !row.clientNo || !row.matterNo)
                      .map((row) => row.overtimeClaimId)
                      .filter((id): id is string => id !== undefined)

                    if (claimStatus === 5 && claimIdsWithoutClientMatter.length > 0) {
                      useToastStore.getState().showToast({
                        type: 'warning',
                        title: 'Manual posting required',
                        text:
                          claimIdsWithoutClientMatter.length === 1
                            ? 'This overtime claim does not have a client/matter number and will need to be posted manually'
                            : 'Some overtime claims do not have a client/matter number and will need to be posted manually'
                      })
                    }

                    setSaving(true)
                    try {
                      await updateOvertimeClaimStatuses({
                        status: claimStatus,
                        overtimeClaimIds,
                        claimIdsWithoutClientMatter
                      })
                    } finally {
                      setSaving(false)
                    }
                  }

                  apiRef.current.setRowSelectionModel([])
                  setClaimStatus(null)
                }}
                sx={{
                  backgroundColor:
                    claimStatus === null || saving ? theme.palette.grey[300] : theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  height: 32,
                  minWidth: 100,
                  mr: 4,
                  mb: 0,
                  '&:hover': {
                    backgroundColor:
                      claimStatus === null || saving ? theme.palette.grey[300] : theme.palette.primary.main
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </MUIButton>
            </>
          )}
        </DataGridSelectionTray>
      )}
    </MUIPaper>
  )
}

type OvertimeTableModuleProps = {
  isSubmissionVersion?: boolean
}

type OvertimeRow = {
  overtimeClaimId?: string
  incurredDate?: string
  claimRecipientUserId?: string
  overtimeClaimTypeDescription?: string
  description?: string
  matterNo?: string
  clientNo?: string
  amount?: number
  hours?: number
  status?: number
}
